import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Checkbox,
  MenuItem,
  Paper,
  Popover,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useFetchObjects from "../../api/useFetchObjects";
import CollectPaymentDialog from "../Customers/CollectPaymentDialog";
import PurchasePaymentAdd from "../PurchasePayments/PurchasePaymentAdd";
import { useTranslation } from "react-i18next";

const getTodayInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const createUiId = () =>
  `bill-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const emptyItem = () => ({
  uiId: createUiId(),
  billNo: "",
  date: getTodayInputValue(),
  description: "",
  unit: "",
  qty: "",
  currency: "",
  total: "",
  paid: "",
  status: "",
  isEditing: true,
  isNew: true,
});

const getStoredItems = (agentId) => {
  try {
    const raw = localStorage.getItem(`agent-items-${agentId}`);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const prepareStoredItems = (storedItems) =>
  storedItems.map((item) => ({
    ...item,
    uiId: item?.uiId || createUiId(),
    isEditing: false,
    status: item?.status || "",
  }));

const sanitizeStoredItems = (storedItems) =>
  storedItems.map((item) => {
    const cleanItem = { ...item };
    delete cleanItem.isEditing;
    delete cleanItem.isNew;
    return cleanItem;
  });

const normalizeList = (data) =>
  Array.isArray(data) ? data : data?.results || [];

const getOptionTitle = (options, value) => {
  const selected = options.find((option) => String(option.id) === String(value));
  return selected?.title || value || "-";
};

const parseMoney = (value) =>
  Number(String(value || "0").replace(/,/g, "")) || 0;

const applyPaymentToItems = (storedItems, amount) => {
  const paymentAmount = parseMoney(amount);
  if (paymentAmount <= 0) return storedItems;

  let remaining = paymentAmount;
  return storedItems.map((item) => {
    if (remaining <= 0) return item;

    const total = parseMoney(item?.total);
    const paid = parseMoney(item?.paid);
    const available = Math.max(total - paid, 0);
    if (available <= 0) return item;

    const applied = Math.min(available, remaining);
    remaining -= applied;

    return {
      ...item,
      paid: paid + applied,
    };
  });
};

const applyPaymentToBill = (storedItems, amount, billRef) => {
  if (!billRef) return applyPaymentToItems(storedItems, amount);

  const paymentAmount = parseMoney(amount);
  if (paymentAmount <= 0) return storedItems;

  return storedItems.map((item) => {
    const matchesBillNo =
      String(item?.billNo || "").trim() &&
      String(item.billNo).trim() === String(billRef).trim();
    const matchesId = String(item?.id || "") === String(billRef);
    if (!matchesBillNo && !matchesId) return item;

    const total = parseMoney(item?.total);
    const paid = parseMoney(item?.paid);
    const available = Math.max(total - paid, 0);
    if (available <= 0) return item;

    const applied = Math.min(available, paymentAmount);
    return {
      ...item,
      paid: paid + applied,
    };
  });
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const createExtraRow = () => ({
  id: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  date: getTodayInputValue(),
  rmb: "",
  rmbRate: "",
  usd: "",
  usdRate: "",
  pkr: "",
  status: "",
  isEditing: true,
});

const EXTRA_ROWS_KEY = (agentId) => `agent-extra-rows-${agentId}`;

const getStoredExtraRows = (agentId) => {
  try {
    const raw = localStorage.getItem(EXTRA_ROWS_KEY(agentId));
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((r) => ({ ...r, isEditing: false })) : [];
  } catch {
    return [];
  }
};

const saveExtraRows = (agentId, rows) => {
  const clean = rows.map(({ isEditing, ...rest }) => rest);
  localStorage.setItem(EXTRA_ROWS_KEY(agentId), JSON.stringify(clean));
};

const ExtraRowsTable = ({ t, currencies, agentId, showTable, setShowTable }) => {
  const [rows, setRows] = useState(() => agentId ? getStoredExtraRows(agentId) : []);

  const persist = (nextRows) => {
    setRows(nextRows);
    if (agentId) saveExtraRows(agentId, nextRows);
  };

  const handleChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleSave = (id) => {
    const nextRows = rows.map((row) => (row.id === id ? { ...row, isEditing: false } : row));
    persist(nextRows);
  };

  const handleEdit = (id) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, isEditing: true } : row))
    );
  };

  const handleToggleStatus = (id) => {
    const nextRows = rows.map((row) => {
      if (row.id !== id) return row;
      if (String(row.status || "").toLowerCase() === "ok") return row;
      return { ...row, status: "ok" };
    });
    persist(nextRows);
  };

  const handleDeleteRow = (id) => {
    const nextRows = rows.filter((row) => row.id !== id);
    persist(nextRows);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {showTable && (
      <>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
          <Button
            variant="outlined"
            color="success"
            size="small"
            onClick={() => persist([...rows, createExtraRow()])}
          >
            + {t("agent.addRow", "Add Row")}
          </Button>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="extra rows table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white", width: 40 }}>{t("agent.no", "No.")}</TableCell>
              <TableCell sx={{ color: "white", minWidth: 130 }}>{t("agent.date", "Date")}</TableCell>
              <TableCell sx={{ color: "white", minWidth: 100 }}>RMB</TableCell>
              <TableCell sx={{ color: "white", minWidth: 90 }}>{t("agent.rmbRate", "Rate")}</TableCell>
              <TableCell sx={{ color: "white", minWidth: 100 }}>USD</TableCell>
              <TableCell sx={{ color: "white", minWidth: 90 }}>{t("agent.usdRate", "Rate")}</TableCell>
              <TableCell sx={{ color: "white", minWidth: 100 }}>PKR</TableCell>
              <TableCell sx={{ color: "white", minWidth: 80 }}>{t("agent.status", "Status")}</TableCell>
              <TableCell sx={{ color: "white", minWidth: 150 }}>{t("agent.action", "Action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: "text.secondary", fontSize: 13, py: 2 }}>
                  {t("agent.noRowsYet", "No rows yet. Click \"Add Row\" to add one.")}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>{index + 1}</TableCell>

                  {/* Date */}
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="date"
                        value={row.date}
                        onChange={(e) => handleChange(row.id, "date", e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                        sx={{ width: 130 }}
                      />
                    ) : (
                      <Typography variant="body2">{row.date || "-"}</Typography>
                    )}
                  </TableCell>

                  {/* RMB */}
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="number"
                        value={row.rmb}
                        onChange={(e) => handleChange(row.id, "rmb", e.target.value)}
                        size="small"
                        placeholder="0"
                        variant="standard"
                        sx={{ width: 90 }}
                      />
                    ) : (
                      <Typography variant="body2">{row.rmb || "-"}</Typography>
                    )}
                  </TableCell>

                  {/* RMB Rate */}
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="number"
                        value={row.rmbRate}
                        onChange={(e) => handleChange(row.id, "rmbRate", e.target.value)}
                        size="small"
                        placeholder="0"
                        variant="standard"
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Typography variant="body2">{row.rmbRate || "-"}</Typography>
                    )}
                  </TableCell>

                  {/* USD */}
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="number"
                        value={row.usd}
                        onChange={(e) => handleChange(row.id, "usd", e.target.value)}
                        size="small"
                        placeholder="0"
                        variant="standard"
                        sx={{ width: 90 }}
                      />
                    ) : (
                      <Typography variant="body2">{row.usd || "-"}</Typography>
                    )}
                  </TableCell>

                  {/* USD Rate */}
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="number"
                        value={row.usdRate}
                        onChange={(e) => handleChange(row.id, "usdRate", e.target.value)}
                        size="small"
                        placeholder="0"
                        variant="standard"
                        sx={{ width: 80 }}
                      />
                    ) : (
                      <Typography variant="body2">{row.usdRate || "-"}</Typography>
                    )}
                  </TableCell>

                  {/* PKR */}
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        type="number"
                        value={row.pkr}
                        onChange={(e) => handleChange(row.id, "pkr", e.target.value)}
                        size="small"
                        placeholder="0"
                        variant="standard"
                        sx={{ width: 90 }}
                      />
                    ) : (
                      <Typography variant="body2">{row.pkr || "-"}</Typography>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={String(row.status || "").toLowerCase() === "ok"}
                      onChange={() => handleToggleStatus(row.id)}
                      disabled={String(row.status || "").toLowerCase() === "ok"}
                      inputProps={{ "aria-label": t("agent.okStatus", "OK status") }}
                      icon={<CheckBoxOutlineBlankOutlinedIcon sx={{ color: "success.main" }} fontSize="small" />}
                      checkedIcon={<CheckBoxIcon sx={{ color: "success.main" }} fontSize="small" />}
                      sx={{ p: 0, "&:hover": { backgroundColor: "transparent" } }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {row.isEditing ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleSave(row.id)}
                          sx={{ minWidth: 52, fontSize: 11, px: 1, py: 0.3 }}
                        >
                          {t("agent.save", "Save")}
                        </Button>
                      ) : (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleEdit(row.id)}
                          aria-label={t("agent.edit", "Edit")}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteRow(row.id)}
                        aria-label={t("agent.delete", "Delete")}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
            {rows.length > 0 && (
              <TableRow sx={{ backgroundColor: "rgba(53, 93, 49, 0.08)", fontWeight: 700 }}>
                <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: 13 }}>
                  {t("agent.total", "Total")}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 13, color: "primary.main" }}>
                  {rows.reduce((sum, r) => sum + (Number(r.rmb) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell />
                <TableCell sx={{ fontWeight: 700, fontSize: 13, color: "primary.main" }}>
                  {rows.reduce((sum, r) => sum + (Number(r.usd) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell />
                <TableCell sx={{ fontWeight: 700, fontSize: 13, color: "primary.main" }}>
                  {rows.reduce((sum, r) => sum + (Number(r.pkr) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </>
      )}
    </Box>
  );
};

const AgentView = ({ agent, open, onClose, token, onSuccess }) => {
  const { t } = useTranslation();
  const agentType = String(agent?.agent_type || "").toLowerCase();
  const isCustomerAgent = agentType === "customer";
  const isSupplierAgent = agentType === "supplier";
  const [items, setItems] = useState([]);
  const [descriptionAnchorEl, setDescriptionAnchorEl] = useState(null);
  const [activeDescription, setActiveDescription] = useState("");
  const [openCollectPayment, setOpenCollectPayment] = useState(false);
  const [openSupplierPayment, setOpenSupplierPayment] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showExtraRows, setShowExtraRows] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [paymentEditValues, setPaymentEditValues] = useState({});
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);
  const summarySectionRef = useRef(null);
  const { data: unitsData } = useFetchObjects(
    ["agent-view-units"],
    "shop/units/?page_size=all"
  );
  const { data: currenciesData } = useFetchObjects(
    ["agent-view-currencies"],
    "shop/currencies/?page_size=all"
  );
  const customerPaymentQuery =
    open && agent?.id
      ? `shop/customer-balance-payments/?customer=${agent.id}&page_size=all`
      : "";
  const supplierPaymentQuery =
    open && agent?.name
      ? `shop/account-transactions/?search=${encodeURIComponent(agent.name)}&transaction_type=debit&page_size=all`
      : "";
  const { data: customerPaymentsData, refetch: refetchCustomerPayments } = useFetchObjects(
    ["agent-view-customer-payments", agent?.id],
    customerPaymentQuery,
    token,
    { enabled: Boolean(customerPaymentQuery) }
  );
  const { data: supplierPaymentsData, refetch: refetchSupplierPayments } = useFetchObjects(
    ["agent-view-supplier-payments", agent?.id],
    supplierPaymentQuery,
    token,
    { enabled: Boolean(supplierPaymentQuery) }
  );
  const units = useMemo(() => normalizeList(unitsData), [unitsData]);
  const currencies = useMemo(() => normalizeList(currenciesData), [currenciesData]);
  const customerPayments = useMemo(
    () => normalizeList(customerPaymentsData),
    [customerPaymentsData]
  );
  const supplierPayments = useMemo(
    () =>
      normalizeList(supplierPaymentsData).filter((payment) => {
        const supplierName = String(payment?.description || "");
        return !agent?.name || supplierName.trim().toLowerCase().includes(String(agent.name).trim().toLowerCase());
      }),
    [supplierPaymentsData, agent?.name]
  );
  const customerPaymentRecords = useMemo(
    () =>
      customerPayments.map((payment) => ({
        recordType: "customer",
        id: payment?.id,
        reference: `#${payment?.id || "-"}`,
        amount: payment?.amount,
        currency: payment?.currency_details?.title || payment?.currency || "-",
        currencyId: payment?.currency_details?.id || payment?.currency || "",
        account: payment?.account_details?.name || "-",
        accountId: payment?.account_details?.id || payment?.account || "",
        exchangeRate: payment?.exchange_rate || "-",
        convertedAmount: payment?.converted_amount || "-",
        paymentDate: payment?.payment_date,
        notes: payment?.notes || "-",
      })),
    [customerPayments]
  );
  const supplierPaymentRecords = useMemo(
      () =>
        supplierPayments.map((payment) => ({
          recordType: "supplier",
          id: payment?.id,
          reference: `#${payment?.id || "-"}`,
          amount: payment?.amount,
          currency: payment?.transaction_currency_title || payment?.transaction_currency || "-",
          account: payment?.account_name || "-",
          exchangeRate: payment?.exchange_rate || "-",
          convertedAmount: payment?.converted_amount || "-",
          paymentDate: payment?.transaction_date,
          notes: payment?.description || "-",
        })),
    [supplierPayments]
  );
  const paymentRecords = useMemo(() => {
    return [...customerPaymentRecords, ...supplierPaymentRecords].sort((a, b) => {
      const aTime = new Date(a.paymentDate || 0).getTime() || 0;
      const bTime = new Date(b.paymentDate || 0).getTime() || 0;
      return bTime - aTime;
    });
  }, [customerPaymentRecords, supplierPaymentRecords]);
  const editingIndex = items.findIndex((item) => item.isEditing);
  const editingItem = editingIndex >= 0 ? items[editingIndex] : null;
  const summary = useMemo(() => {
    const totalQty = items.reduce((sum, item) => sum + parseMoney(item.qty), 0);
    const currencyMap = new Map();

    items.forEach((item) => {
      const currency = getOptionTitle(currencies, item.currency);
      const key = String(item.currency || currency || "-");
      if (!currencyMap.has(key)) {
        currencyMap.set(key, {
          key,
          currency: currency || key,
          totalQty: 0,
          totalAmount: 0,
          totalPaid: 0,
        });
      }

      const entry = currencyMap.get(key);
      entry.totalQty += parseMoney(item.qty);
      entry.totalAmount += parseMoney(item.total);
      entry.totalPaid += parseMoney(item.paid);
    });

    const currencyBreakdown = Array.from(currencyMap.values()).map((entry) => ({
      ...entry,
      totalRemaining: Math.max(entry.totalAmount - entry.totalPaid, 0),
    }));

    const totalAmount = currencyBreakdown.reduce((sum, entry) => sum + entry.totalAmount, 0);
    const totalPaid = currencyBreakdown.reduce((sum, entry) => sum + entry.totalPaid, 0);
    return {
      totalQty,
      totalAmount,
      totalPaid,
      totalRemaining: totalAmount - totalPaid,
      currencyBreakdown,
    };
  }, [currencies, items]);
  useEffect(() => {
    if (!agent?.id || !open) return;
    const syncItems = () => {
      const backendItems = Array.isArray(agent?.bill_items) ? agent.bill_items : [];
      const legacyItems = getStoredItems(agent.id);
      const nextItems = backendItems.length > 0 ? backendItems : legacyItems;
      setItems(prepareStoredItems(nextItems));
    };

    syncItems();
    const backendItems = Array.isArray(agent?.bill_items) ? agent.bill_items : [];
    const legacyItems = getStoredItems(agent.id);
    if (backendItems.length === 0 && legacyItems.length > 0) {
      axios
        .patch(
          `${process.env.REACT_APP_API_BASE_URL}/shop/customers/${agent.id}/`,
          { bill_items: sanitizeStoredItems(legacyItems) },
          { headers: { Authorization: `Token ${token}` } }
        )
        .catch((error) => {
          console.error("Failed to sync legacy bill items:", error);
        });
    }
    window.addEventListener("storage", syncItems);
    window.addEventListener("agent-items-updated", syncItems);
    return () => {
      window.removeEventListener("storage", syncItems);
      window.removeEventListener("agent-items-updated", syncItems);
    };
  }, [agent?.id, agent?.bill_items, open, token]);

  const saveItems = (nextItems, shouldPersist = true) => {
    setItems(nextItems);
    if (agent?.id && shouldPersist) {
      const cleanItems = sanitizeStoredItems(nextItems);
      localStorage.setItem(`agent-items-${agent.id}`, JSON.stringify(cleanItems));
      return axios
        .patch(
          `${process.env.REACT_APP_API_BASE_URL}/shop/customers/${agent.id}/`,
          { bill_items: cleanItems },
          { headers: { Authorization: `Token ${token}` } }
        )
        .catch((error) => {
          console.error("Failed to save bill items:", error);
          return null;
        });
    }
    return Promise.resolve();
  };

  const handleAddRow = () => {
    if (editingIndex >= 0) return;
    saveItems([...items, emptyItem()], false);
  };

  const handleEditRow = (index) => {
    if (editingIndex >= 0) return;
    const nextItems = items.map((item, itemIndex) => ({
      ...item,
      isEditing: itemIndex === index,
    }));
    saveItems(nextItems, false);
  };

  const handleDeleteRow = (index) => {
    const row = items[index];
    if (!row) return;
    setRowToDelete({ index, row });
    setOpenDeleteConfirm(true);
  };

  const handleConfirmDeleteRow = () => {
    if (rowToDelete === null) return;
    const nextItems = items.filter((_, itemIndex) => itemIndex !== rowToDelete.index);
    saveItems(nextItems);
    setOpenDeleteConfirm(false);
    setRowToDelete(null);
  };

  const handleCancelDeleteRow = () => {
    setOpenDeleteConfirm(false);
    setRowToDelete(null);
  };

  const handleChange = (index, field, value) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    );
    saveItems(nextItems, false);
  };

  const handleMarkOk = (index) => {
    const item = items[index];
    if (!item || String(item.status || "").toLowerCase() === "ok") return;

    const nextItems = items.map((currentItem, itemIndex) =>
      itemIndex === index ? { ...currentItem, status: "ok" } : currentItem
    );
    saveItems(nextItems);
  };

  const handleSave = async () => {
    const nextItems = items.map((item) => {
      const cleanItem = { ...item };
      delete cleanItem.isEditing;
      delete cleanItem.isNew;
      return cleanItem;
    });
    const savedAgent = await saveItems(nextItems);
    onSuccess?.(savedAgent?.data || { ...agent, bill_items: nextItems });
  };

  const handleCancelEdit = () => {
    if (!agent?.id) return;
    setItems(prepareStoredItems(getStoredItems(agent.id)));
  };

  const handlePaymentSuccess = (paymentResult) => {
    const nextItems = applyPaymentToItems(
      items,
      paymentResult?.amount
    );
    saveItems(nextItems);
    refetchCustomerPayments?.();
    onSuccess?.({ ...agent, bill_items: nextItems, lastPayment: paymentResult });
  };

  const handleSupplierPaymentSuccess = (paymentResult) => {
    const nextItems = paymentResult?.billNo || paymentResult?.billId
      ? applyPaymentToBill(items, paymentResult?.amount, paymentResult?.billNo || paymentResult?.billId)
      : applyPaymentToItems(items, paymentResult?.amount);
    saveItems(nextItems);
    refetchSupplierPayments?.();
    onSuccess?.({ ...agent, bill_items: nextItems, lastPayment: paymentResult });
    setOpenSupplierPayment(false);
  };

  const handleOpenDescription = (event, description) => {
    setDescriptionAnchorEl(event.currentTarget);
    setActiveDescription(description || "-");
  };

  const handleCloseDescription = () => {
    setDescriptionAnchorEl(null);
    setActiveDescription("");
  };

  const handleEditPayment = (record) => {
    setEditingPayment(record);
    setPaymentEditValues({
      amount: record.amount || "",
      notes: record.notes === "-" ? "" : record.notes || "",
    });
  };

  const handleSavePayment = async () => {
    if (!editingPayment) return;
    setPaymentActionLoading(true);
    try {
      const endpoint = editingPayment.recordType === "customer"
        ? `shop/customer-balance-payments/${editingPayment.id}/`
        : `shop/account-transactions/${editingPayment.id}/`;
      const payload = editingPayment.recordType === "customer"
        ? { amount: paymentEditValues.amount, notes: paymentEditValues.notes }
        : { amount: paymentEditValues.amount, description: paymentEditValues.notes };
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api"}/${endpoint}`,
        payload,
        { headers: { Authorization: `Token ${token}` } }
      );
      refetchCustomerPayments?.();
      refetchSupplierPayments?.();
      setEditingPayment(null);
    } catch (err) {
      console.error("Failed to update payment:", err);
    }
    setPaymentActionLoading(false);
  };

  const handleDeletePayment = async () => {
    if (!deletingPayment) return;
    setPaymentActionLoading(true);
    try {
      const endpoint = deletingPayment.recordType === "customer"
        ? `shop/customer-balance-payments/${deletingPayment.id}/`
        : `shop/account-transactions/${deletingPayment.id}/`;
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api"}/${endpoint}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      refetchCustomerPayments?.();
      refetchSupplierPayments?.();
      setDeletingPayment(null);
    } catch (err) {
      console.error("Failed to delete payment:", err);
    }
    setPaymentActionLoading(false);
  };

  const renderAgentHeaderHtml = (billLabel = "", billNo = "") => {
    const agentName = escapeHtml(agent?.name || "-");
    const agentNumber = escapeHtml(agent?.contact || agent?.number || "-");
    const agentAddress = escapeHtml(agent?.address || "-");

    return `
      <div class="print-header">
        <div>
          <div class="agent-name">${agentName}</div>
          <div class="agent-line">${agentNumber}</div>
          <div class="agent-line">${agentAddress}</div>
        </div>
        <div class="bill-ref">
          <div class="bill-label">${escapeHtml(billLabel)}</div>
          <div class="bill-number">${billNo ? `#${escapeHtml(String(billNo))}` : "&nbsp;"}</div>
        </div>
      </div>
    `;
  };

  const renderPrintFooterHtml = () => `
    <div class="print-footer">
      <div class="footer-thanks">${escapeHtml(t("agent.thankYou", "Thank you for your business!"))}</div>
      <div class="footer-copy">${escapeHtml(t("agent.printFooterCopy", "© 2026 Gilamand MIS - All rights reserved"))}</div>
      <div class="footer-powered">${escapeHtml(t("agent.printFooterPowered", "Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com"))}</div>
    </div>
  `;

  const handlePrintRow = (item) => {
    const unitTitle = getOptionTitle(units, item.unit);
    const currencyTitle = getOptionTitle(currencies, item.currency);
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    const closePrintWindow = () => {
      try {
        if (!printWindow.closed) printWindow.close();
      } catch {
        // ignore cross-window timing issues
      }
    };

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(t("agent.bill", "BILL"))} ${escapeHtml(item.billNo || "")}</title>
          <style>
            @page { margin: 16mm; }
            body { font-family: Arial, sans-serif; padding: 0; color: #111; }
            .page { min-height: calc(100vh - 32mm); padding: 0 14px; display: flex; flex-direction: column; }
            .print-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 10px; border-bottom: 3px solid #2f7ff4; margin-bottom: 18px; }
            .agent-name { font-size: 24px; font-weight: 700; color: #2f7ff4; margin-bottom: 6px; }
            .agent-line { font-size: 13px; color: #666; line-height: 1.5; }
            .bill-ref { text-align: right; }
            .bill-label { font-size: 14px; font-weight: 700; color: #2f7ff4; letter-spacing: 0.04em; }
            .bill-number { margin-top: 6px; font-size: 18px; color: #666; }
            h2 { margin: 0 0 18px; text-align: center; color: #2f7ff4; font-size: 28px; }
            table { border-collapse: collapse; width: 100%; margin-top: 18px; }
            th, td { border: 1px solid #111; padding: 10px; text-align: left; }
            th { background: #e8efe5; }
            .meta { margin-bottom: 6px; }
            .print-footer { margin-top: auto; padding-top: 24px; text-align: center; }
            .footer-thanks { color: #2f7ff4; font-size: 16px; font-weight: 700; margin-bottom: 8px; }
            .footer-copy { color: #666; font-size: 12px; margin-bottom: 4px; }
            .footer-powered { color: #9aa3af; font-size: 11px; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <div class="page">
            ${renderAgentHeaderHtml(t("agent.bill", "BILL"), item?.billNo || "")}
            <h2>${escapeHtml(t("agent.billDetails", "Bill Details"))}</h2>
            <div class="meta"><strong>${escapeHtml(t("agent.typeLabel", "Type"))}:</strong> ${escapeHtml(agent?.agent_type === "supplier" ? t("agent.supplier", "Supplier") : t("agent.customer", "Customer"))}</div>
            <table>
              <thead>
                <tr>
                  <th>${escapeHtml(t("agent.billNo", "Bill No."))}</th>
                  <th>${escapeHtml(t("agent.date", "Date"))}</th>
                  <th>${escapeHtml(t("agent.description", "Description"))}</th>
                  <th>${escapeHtml(t("agent.unit", "Unit"))}</th>
                  <th>${escapeHtml(t("agent.qty", "Qty"))}</th>
                  <th>${escapeHtml(t("agent.currency", "Currency"))}</th>
                  <th>${escapeHtml(t("agent.total", "Total"))}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${escapeHtml(item.billNo || "-")}</td>
                  <td>${escapeHtml(formatDate(item.date))}</td>
                  <td>${escapeHtml(item.description || "-")}</td>
                  <td>${escapeHtml(unitTitle)}</td>
                  <td>${escapeHtml(item.qty || "-")}</td>
                  <td>${escapeHtml(currencyTitle)}</td>
                  <td>${escapeHtml(item.total || "-")}</td>
                </tr>
              </tbody>
            </table>
            ${renderPrintFooterHtml()}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
              window.addEventListener('afterprint', function() { window.close(); });
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onafterprint = closePrintWindow;
  };

  const handlePrintSummary = () => {
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    const closePrintWindow = () => {
      try {
        if (!printWindow.closed) printWindow.close();
      } catch {
        // ignore cross-window timing issues
      }
    };

    const tableRows = items
      .map(
        (item) => `
          <tr>
            <td>${escapeHtml(item.billNo || "-")}</td>
            <td>${escapeHtml(formatDate(item.date))}</td>
            <td>${escapeHtml(item.description || "-")}</td>
            <td>${escapeHtml(getOptionTitle(units, item.unit))}</td>
            <td>${escapeHtml(item.qty || "-")}</td>
            <td>${escapeHtml(getOptionTitle(currencies, item.currency))}</td>
            <td>${escapeHtml(item.total || "-")}</td>
          </tr>
        `
      )
      .join("");

    const currencySummaryRows = summary.currencyBreakdown
      .map(
        (row) => `
          <tr>
            <td class="summary-label">${escapeHtml(row.currency)}</td>
            <td class="summary-value">${escapeHtml(formatNumber(row.totalQty))}</td>
            <td class="summary-value">${escapeHtml(formatNumber(row.totalAmount))}</td>
            <td class="summary-value paid">${escapeHtml(formatNumber(row.totalPaid))}</td>
          </tr>
        `
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Payment Records</title>
          <style>
            @page { margin: 16mm; }
            body { font-family: Arial, sans-serif; padding: 0; color: #111; }
            .page { min-height: calc(100vh - 32mm); padding: 0 14px; display: flex; flex-direction: column; }
            .print-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 10px; border-bottom: 3px solid #2f7ff4; margin-bottom: 18px; }
            .agent-name { font-size: 24px; font-weight: 700; color: #2f7ff4; margin-bottom: 6px; }
            .agent-line { font-size: 13px; color: #666; line-height: 1.5; }
            .bill-ref { text-align: right; }
            .bill-label { font-size: 14px; font-weight: 700; color: #2f7ff4; letter-spacing: 0.04em; }
            .bill-number { margin-top: 6px; font-size: 18px; color: #666; }
            h2 { margin: 0 0 18px; text-align: center; color: #2f7ff4; font-size: 28px; }
            .bill-table { border-collapse: collapse; width: 100%; margin-top: 18px; }
            .summary-table { border-collapse: collapse; width: 100%; margin-top: 12px; table-layout: fixed; }
            th, td { border: 1px solid #111; padding: 10px; text-align: left; }
            th { background: #e8efe5; }
            .meta { margin-bottom: 6px; }
            .summary-wrap { margin-top: 18px; display: flex; justify-content: flex-start; }
            .summary-card { border: 1px solid #2f5d2f; border-radius: 4px; padding: 8px; width: fit-content; max-width: 360px; }
            .summary-total { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-weight: 700; font-size: 12px; }
            .summary-table td, .summary-table th { border-color: #d7e3d7; }
            .summary-table th { font-size: 10.5px; font-weight: 700; background: #e8efe5; padding: 7px 6px; }
            .summary-table td { padding: 7px 6px; font-size: 11px; }
            .summary-label { font-weight: 700; font-size: 11px; }
            .summary-value { text-align: right; font-weight: 700; font-size: 11px; }
            .paid { color: #2e7d32; }
            .remaining { color: #d32f2f; }
            .print-footer { margin-top: auto; padding-top: 24px; text-align: center; }
            .footer-thanks { color: #2f7ff4; font-size: 16px; font-weight: 700; margin-bottom: 8px; }
            .footer-copy { color: #666; font-size: 12px; margin-bottom: 4px; }
            .footer-powered { color: #9aa3af; font-size: 11px; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <div class="page">
            ${renderAgentHeaderHtml(t("agent.totals", "TOTALS"))}
            <h2>${escapeHtml(t("agent.paymentRecords", "Payment Records"))}</h2>
            <div class="meta"><strong>${escapeHtml(t("agent.typeLabel", "Type"))}:</strong> ${escapeHtml(agent?.agent_type === "supplier" ? t("agent.supplier", "Supplier") : t("agent.customer", "Customer"))}</div>
            <table class="bill-table">
              <thead>
                <tr>
                  <th>${escapeHtml(t("agent.billNo", "Bill No."))}</th>
                  <th>${escapeHtml(t("agent.date", "Date"))}</th>
                  <th>${escapeHtml(t("agent.description", "Description"))}</th>
                  <th>${escapeHtml(t("agent.unit", "Unit"))}</th>
                  <th>${escapeHtml(t("agent.qty", "Qty"))}</th>
                  <th>${escapeHtml(t("agent.currency", "Currency"))}</th>
                  <th>${escapeHtml(t("agent.total", "Total"))}</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
            <div class="summary-wrap">
              <div class="summary-card">
                <div class="summary-total">
                  <span>${escapeHtml(t("agent.totalQty", "Total Qty"))}</span>
                  <span>${escapeHtml(formatNumber(summary.totalQty))}</span>
                </div>
                <table class="summary-table">
                  <thead>
                    <tr>
                      <th>${escapeHtml(t("agent.currency", "Currency"))}</th>
                      <th style="width: 18%;">${escapeHtml(t("agent.qty", "Qty"))}</th>
                      <th style="width: 28%;">${escapeHtml(t("agent.total", "Total"))}</th>
                      <th style="width: 28%;">${escapeHtml(t("agent.paid", "Paid"))}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${currencySummaryRows || ""}
                  </tbody>
                </table>
              </div>
            </div>
            ${renderPrintFooterHtml()}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
              window.addEventListener('afterprint', function() { window.close(); });
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onafterprint = closePrintWindow;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      {editingItem ? (
        <DialogTitle>{t("agent.addBillItem", "Add Bill Item")}</DialogTitle>
      ) : (
        <DialogTitle>
          {isSupplierAgent
            ? t("agent.paymentRecords", "Payment Records")
            : t("agent.agentTotals", "Agent Totals")}
        </DialogTitle>
      )}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {editingItem ? (
          <Box sx={{ display: "grid", gap: 2.25, pt: 0.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
              <TextField
                label={t("agent.billNo", "Bill No.")}
                value={editingItem.billNo}
                placeholder={t("agent.enterBillNo", "Enter bill no")}
                onChange={(event) =>
                  handleChange(editingIndex, "billNo", event.target.value)
                }
                fullWidth
              />
              <TextField
                label={t("agent.date", "Date")}
                type="date"
                value={editingItem.date}
                onChange={(event) =>
                  handleChange(editingIndex, "date", event.target.value)
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Select
                displayEmpty
                value={editingItem.unit}
                onChange={(event) =>
                  handleChange(editingIndex, "unit", event.target.value)
                }
                fullWidth
              >
                <MenuItem value="">{t("agent.selectUnit", "Select unit")}</MenuItem>
                {units.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.title}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <TextField
              label={t("agent.description", "Description")}
              value={editingItem.description}
              placeholder={t("agent.enterDescription", "Enter description")}
              onChange={(event) =>
                handleChange(editingIndex, "description", event.target.value)
              }
              fullWidth
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
              <TextField
                label={t("agent.qty", "Qty")}
                type="number"
                value={editingItem.qty}
                placeholder={t("agent.enterQty", "Enter qty")}
                onChange={(event) =>
                  handleChange(editingIndex, "qty", event.target.value)
                }
                fullWidth
              />
              <Select
                displayEmpty
                value={editingItem.currency}
                onChange={(event) =>
                  handleChange(editingIndex, "currency", event.target.value)
                }
              >
                <MenuItem value="">{t("agent.selectCurrency", "Select currency")}</MenuItem>
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.title}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                label={t("agent.total", "Total")}
                value={editingItem.total}
                placeholder={t("agent.enterTotal", "Enter total")}
                onChange={(event) =>
                  handleChange(editingIndex, "total", event.target.value)
                }
                fullWidth
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 2.25, pt: 0.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <Button variant="contained" color="primary" onClick={handleAddRow}>
                {t("agent.addBillItem", "Add Bill Item")}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="agent bill items">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.main" }}>
                    <TableCell sx={{ color: "white" }}>{t("agent.billNo", "Bill No.")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.date", "Date")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.description", "Description")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.unit", "Unit")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.qty", "Qty")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.currency", "Currency")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.total", "Total")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.status", "Status")}</TableCell>
                    <TableCell sx={{ color: "white" }}>{t("agent.action", "Action")}</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {t("agent.noBillItemsYet", "No bill items yet.")}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow key={`agent-item-${index}`}>
                      <TableCell>{item.billNo || "-"}</TableCell>
                      <TableCell>{formatDate(item.date)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(event) => handleOpenDescription(event, item.description)}
                          aria-label={t("agent.viewDescription", "Show description")}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        {getOptionTitle(units, item.unit)}
                      </TableCell>
                      <TableCell>{item.qty || "-"}</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        {getOptionTitle(currencies, item.currency)}
                      </TableCell>
                      <TableCell>{item.total || "-"}</TableCell>
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={String(item.status || "").toLowerCase() === "ok"}
                          onChange={() => handleMarkOk(index)}
                          inputProps={{ "aria-label": t("agent.okStatus", "OK status") }}
                          icon={<CheckBoxOutlineBlankOutlinedIcon sx={{ color: "success.main" }} fontSize="small" />}
                          checkedIcon={<CheckBoxIcon sx={{ color: "success.main" }} fontSize="small" />}
                          sx={{
                            p: 0,
                            "&:hover": { backgroundColor: "transparent" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlePrintRow(item)}
                            aria-label={t("agent.print", "Print")}
                          >
                            <PrintOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleEditRow(index)}
                            aria-label={t("agent.edit", "Edit")}
                            disabled={editingIndex >= 0}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRow(index)}
                            aria-label={t("agent.delete", "Delete")}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Popover
              open={Boolean(descriptionAnchorEl)}
              anchorEl={descriptionAnchorEl}
              onClose={handleCloseDescription}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  p: 2.25,
                  minWidth: 240,
                  maxWidth: 320,
                  borderRadius: 1,
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", px: 1, py: 0.5 }}>
                {activeDescription || "-"}
              </Box>
            </Popover>
            <Box sx={{ display: "flex", justifyContent: "flex-start" }} ref={summarySectionRef}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: "success.main",
                  borderRadius: 0.75,
                  borderWidth: 1,
                  width: "fit-content",
                  minWidth: 205,
                  maxWidth: 380,
                  alignSelf: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.15 }}>
                  <Typography sx={{ fontWeight: 700, color: "text.secondary", fontSize: 10.5 }}>
                    {t("agent.totalQty", "Total Qty")}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "text.primary", fontSize: 11.5 }}>
                    {formatNumber(summary.totalQty)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.68 }}>
                  {[
                    { label: t("agent.totalAmount", "Total Amount"), valueKey: "totalAmount", valueColor: "text.primary", chipBorder: "success.main", chipBg: "rgba(53, 93, 49, 0.04)" },
                    { label: t("agent.totalPaid", "Total Paid"), valueKey: "totalPaid", valueColor: "success.main", chipBorder: "success.main", chipBg: "rgba(46, 125, 50, 0.05)" },
                    { label: t("agent.totalRemaining", "Total Remaining"), valueKey: "totalRemaining", valueColor: "error.main", chipBorder: "error.main", chipBg: "rgba(211, 47, 47, 0.05)" },
                  ].map((metric, index, list) => (
                    <Box
                      key={metric.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.2,
                        mb: index === list.length - 1 ? 0 : 0.42,
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 112,
                          fontWeight: 700,
                          fontSize: 10.25,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {metric.label}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          justifyContent: "flex-end",
                          flex: 1,
                        }}
                      >
                        {summary.currencyBreakdown.length > 0 ? (
                          summary.currencyBreakdown.map((row) => (
                            <Box
                              key={`${metric.label}-${row.key}`}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.45,
                                px: 0.85,
                                py: 0.3,
                                minWidth: 64,
                                borderRadius: 999,
                                border: "1px solid",
                                borderColor: metric.chipBorder,
                                bgcolor: metric.chipBg,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Box sx={{ fontWeight: 700, fontSize: 8.75, color: "text.secondary", lineHeight: 1 }}>
                                {row.currency}
                              </Box>
                              <Box
                                sx={{
                                  fontWeight: 700,
                                  fontSize: 9.75,
                                  color: metric.valueColor,
                                  lineHeight: 1,
                                }}
                              >
                                {formatNumber(row[metric.valueKey])}
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Typography sx={{ color: "text.secondary", fontSize: 11.5 }}>
                            -
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>

            {/* Extra rows table below the summary box — customers only */}
            {isCustomerAgent && <ExtraRowsTable t={t} currencies={currencies} agentId={agent?.id} showTable={showExtraRows} setShowTable={setShowExtraRows} />}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {editingItem ? (
          <>
            <Button onClick={handleCancelEdit} color="secondary">
              {t("agent.cancel", "Cancel")}
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {t("agent.submit", "Submit")}
            </Button>
          </>
        ) : (
          <>
            {isCustomerAgent && (
              <Button
                onClick={() => setOpenCollectPayment(true)}
                variant="contained"
                color="success"
              >
                {t("agent.collectPayment", "Customer Payment")}
              </Button>
            )}
            {isCustomerAgent && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowExtraRows((prev) => !prev)}
              >
                {t("agent.row", "Row")}
              </Button>
            )}
            {isSupplierAgent && (
              <Button
                onClick={() => setOpenSupplierPayment(true)}
                variant="contained"
                color="success"
              >
                {t("agent.paySupplier", "Supplier Payment")}
              </Button>
            )}
            <Button onClick={handlePrintSummary} variant="outlined" color="primary">
              {t("agent.print", "Print")}
            </Button>
            <Button onClick={onClose} color="secondary">
              {t("agent.close", "Close")}
            </Button>
          </>
        )}
      </DialogActions>
      {openCollectPayment && (
        <CollectPaymentDialog
          open={openCollectPayment}
          onClose={() => setOpenCollectPayment(false)}
          customer={agent}
          token={token}
          onSuccess={handlePaymentSuccess}
          summary={summary}
          billRows={items}
          paymentRecords={paymentRecords}
          onEditPayment={handleEditPayment}
          onDeletePayment={setDeletingPayment}
        />
      )}
      {openSupplierPayment && (
        <PurchasePaymentAdd
          open={openSupplierPayment}
          onClose={() => setOpenSupplierPayment(false)}
          token={token}
          initialSupplierId={agent?.id}
          initialSupplierName={agent?.name}
          initialSupplierContact={agent?.contact}
          summary={summary}
          billRows={items}
          onSuccess={handleSupplierPaymentSuccess}
          paymentRecords={paymentRecords}
          onEditPayment={handleEditPayment}
          onDeletePayment={setDeletingPayment}
        />
      )}
      <Dialog open={openDeleteConfirm} onClose={handleCancelDeleteRow}>
        <DialogTitle>{t("agent.delete", "Delete")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              "agent.deleteConfirmMessage",
              "Are you sure you want to delete this bill item?"
            )}
          </Typography>
          {rowToDelete?.row && (
            <Box sx={{ mt: 1.5, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                #{rowToDelete.row.billNo || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(rowToDelete.row.date)} | {getOptionTitle(units, rowToDelete.row.unit)} | {getOptionTitle(currencies, rowToDelete.row.currency)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteRow} color="secondary">
            {t("agent.cancel", "Cancel")}
          </Button>
          <Button onClick={handleConfirmDeleteRow} color="error" variant="contained">
            {t("agent.deleteNow", "Delete Now")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment delete confirmation */}
      <Dialog open={Boolean(deletingPayment)} onClose={() => setDeletingPayment(null)}>
        <DialogTitle>{t("agent.delete", "Delete Payment")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("agent.deletePaymentConfirm", "Are you sure you want to delete this payment record?")}
          </Typography>
          {deletingPayment && (
            <Box sx={{ mt: 1.5, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {deletingPayment.reference} — {Number(deletingPayment.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {deletingPayment.currency}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(deletingPayment.paymentDate)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingPayment(null)} color="secondary" disabled={paymentActionLoading}>
            {t("agent.cancel", "Cancel")}
          </Button>
          <Button onClick={handleDeletePayment} color="error" variant="contained" disabled={paymentActionLoading}>
            {paymentActionLoading ? <CircularProgress size={20} color="inherit" /> : t("agent.deleteNow", "Delete Now")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AgentView;
